// fetchMessageSegments.js
'use strict';
import fetchResource from './fetchResource.js';
import getMessageData from '../selector/getMessageData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import fetchMediaMenifest from '../resource/fetchMediaMenifest.js';
import parseM3u8 from '../resource/parseM3u8.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import { PUBLIC_MESSAGE_URL_PATH } from '../RemoteConfigKeys.js';

/**
 * The `uri` field in the `AUDIO` object is supposed to be under: AUDIO.{GROUP-ID}.{NAME}.uri
 * However the `GROUP-ID` and `NAME` seems inconstant.
 * For instance, it used to be AUDIO['default-audio-group'].stream_0.uri before,
 * but it's AUDIO.sd.sd.uri now.
 * That's why we flatten the `AUDIO` object to get `uri` to avoid this issue.
 * @param {object} audioObject - the object needs to be flattened
 * @returns {object} returns a flatted object
 */
const flattenAudioObject = (audioObject = {}) => {
  const flatted = {};
  for (const [key, value] of Object.entries(audioObject)) {
    if (typeof value === 'object' && value !== null) {
      Object.assign(flatted, flattenAudioObject(value));
    } else {
      flatted[key] = value;
    }
  }
  return flatted;
};

/**
 * To fetch segments for a specific message.
 * @param {string} {messageId} - the post message ID.
 * @param {string} {manifestType} - the type of manifest (mpd|m3u8)
 */
const fetchMessageSegments =
  ({ messageId, manifestType = 'mpd' }) =>
  async (dispatch, getState) => {
    if (!messageId) return;
    const state = getState();
    const assetId = getMessageData(state, messageId, 'assetIds')?.[0];
    if (!assetId) return;

    const response = await fetchMediaMenifest({
      id: messageId,
      assetId,
      type: manifestType,
    });

    if (manifestType === 'm3u8') {
      try {
        const publicMessageUrlPath = getRemoteConfigData(
          getState(),
          PUBLIC_MESSAGE_URL_PATH
        );
        const videoUriPathname = response.m3u8?.playlists?.[0]?.uri;
        const videoUri = getResourceUrl({
          path: publicMessageUrlPath,
          endpoint: `/${messageId}/${assetId}/${videoUriPathname}`,
          resourceType: PUBLIC,
        })?.href;

        const flattedAudioObject = flattenAudioObject(
          response.m3u8?.mediaGroups?.AUDIO
        );
        const audioUriPathname = flattedAudioObject?.uri;
        const audioUri = audioUriPathname
          ? getResourceUrl({
              path: publicMessageUrlPath,
              endpoint: `/${messageId}/${assetId}/${audioUriPathname}`,
              resourceType: PUBLIC,
            })?.href
          : null;

        // Parse m3u8 master manifests
        const m3u8ResponseData = await Promise.all([
          videoUri && fetch(videoUri),
          audioUri && fetch(audioUri),
        ]);
        const [videoText, audioText] = await Promise.all(
          m3u8ResponseData.map(data => (data ? data.text?.() : ''))
        );
        if (videoUri && videoText) {
          const videoMapUri = videoText.match(/EXT-X-MAP:URI="(.*)"/)?.[1];
          const videoSegment = videoUri.replace(
            videoUriPathname,
            videoUriPathname.replace(/\/(.*)\.m3u8/, `/${videoMapUri}`)
          );
          if (videoSegment) dispatch(fetchResource(videoSegment));
          const parsedVideoObject = await parseM3u8({ data: videoText });
          const videoSegmentUri1 = parsedVideoObject?.segments?.[0]?.uri;
          if (videoSegmentUri1) {
            const videoSegment1 = videoUri.replace(
              videoUriPathname,
              videoUriPathname.replace(/\/(.*)\.m3u8/, `/${videoSegmentUri1}`)
            );
            dispatch(fetchResource(videoSegment1));
          }
          const videoSegmentUri2 = parsedVideoObject?.segments?.[1]?.uri;
          if (videoSegmentUri2) {
            const videoSegment2 = videoUri.replace(
              videoUriPathname,
              videoUriPathname.replace(/\/(.*)\.m3u8/, `/${videoSegmentUri2}`)
            );
            dispatch(fetchResource(videoSegment2));
          }
        }
        if (audioUri && audioText) {
          const audioMapUri = audioText.match(/EXT-X-MAP:URI="(.*)"/)?.[1];
          const audioSegments = audioUri.replace(
            audioUriPathname,
            audioUriPathname.replace(/\/(.*)\.m3u8/, `/${audioMapUri}`)
          );
          if (audioSegments) dispatch(fetchResource(audioSegments));
          const parsedAudioObject = await parseM3u8({ data: audioText });
          const audioSegmentUri1 = parsedAudioObject?.segments?.[0]?.uri;
          if (audioSegmentUri1) {
            const audioSegment1 = audioUri.replace(
              audioUriPathname,
              audioUriPathname.replace(/\/(.*)\.m3u8/, `/${audioSegmentUri1}`)
            );
            dispatch(fetchResource(audioSegment1));
          }
          const audioSegmentUri2 = parsedAudioObject?.segments?.[1]?.uri;
          if (audioSegmentUri2) {
            const audioSegment2 = audioUri.replace(
              audioUriPathname,
              audioUriPathname.replace(/\/(.*)\.m3u8/, `/${audioSegmentUri2}`)
            );
            dispatch(fetchResource(audioSegment2));
          }
        }
      } catch (error) {
        // console.log('Failed to parse m3u8');
      }

      return;
    }

    const videoSegments = response.mpd?.playlists?.[0]?.segments;
    const audioSegments =
      response.mpd?.mediaGroups?.AUDIO?.audio?.main?.playlists?.[0]?.segments;
    const videoURIs = videoSegments
      ? [
          videoSegments[0]?.map?.resolvedUri,
          videoSegments[0]?.resolvedUri,
          videoSegments[1]?.resolvedUri,
          videoSegments[2]?.resolvedUri,
        ]
      : [];
    const audioURIs = audioSegments
      ? [
          audioSegments[0]?.map?.resolvedUri,
          audioSegments[0]?.resolvedUri,
          audioSegments[1]?.resolvedUri,
          audioSegments[2]?.resolvedUri,
        ]
      : [];

    [...videoURIs, ...audioURIs]?.forEach(uri => {
      uri && dispatch(fetchResource(uri));
    });
  };

export default fetchMessageSegments;
